/** Order-level POSTAGE rows (one shipment fee per order, not per voucher line). */

export function sumOrderPostageDiscounts(
  discounts: Array<{ discountType?: string; discountValue?: number | null }> | undefined | null,
): number {
  return (discounts ?? [])
    .filter((d) => d.discountType === 'POSTAGE')
    .reduce((sum, d) => sum + (d.discountValue ?? 0), 0)
}

export type VoucherCartPostageInput = {
  id: string
  deliveryMethod?: string | null
  orderId?: string | null
  createdAt?: string | null
  orderPostage?: number
}

/** Assign the full order postage to the first physical voucher only (by createdAt). */
export function buildVoucherCartPostageFeeMap(
  vouchers: VoucherCartPostageInput[],
): Map<string, number> {
  const fees = new Map<string, number>()
  const assignedOrders = new Set<string>()

  const sorted = [...vouchers].sort((a, b) =>
    String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? '')),
  )

  for (const voucher of sorted) {
    if ((voucher.deliveryMethod ?? '').toUpperCase() !== 'PHYSICAL') continue
    if (!voucher.orderId || assignedOrders.has(voucher.orderId)) continue

    const postage = voucher.orderPostage ?? 0
    if (postage > 0) {
      fees.set(voucher.id, postage)
      assignedOrders.add(voucher.orderId)
    }
  }

  return fees
}

export function computeCartTotals(items: Array<{ price: number; postageFee?: number | null }>) {
  const subtotal = items.reduce((sum, item) => sum + item.price, 0)
  const postageTotal = items.reduce((sum, item) => sum + (item.postageFee ?? 0), 0)
  return { subtotal, postageTotal, total: subtotal + postageTotal }
}

/** Resolve the order's postage option from an existing cart (one shipment fee per order). */
export function findSharedOrderPostageOptionIdFromCart(
  items: Array<{
    type?: string
    deliveryMethod?: string | null
    orderPostageOptionName?: string | null
    orderPostageFee?: number | null
  }>,
  postageOptions: Array<{ id: string; name: string; fee: number }>,
): string | undefined {
  for (const item of items) {
    if (item.type !== 'VOUCHER') continue
    const delivery = (item.deliveryMethod ?? '').toUpperCase()
    if (delivery !== 'PHYSICAL' && delivery !== 'POST') continue
    if (!item.orderPostageOptionName && item.orderPostageFee == null) continue

    if (item.orderPostageOptionName) {
      const byName = postageOptions.find(
        (option) => option.name.trim() === item.orderPostageOptionName?.trim(),
      )
      if (byName) return byName.id
    }

    if (item.orderPostageFee != null) {
      const byFeeMatches = postageOptions.filter((option) => option.fee === item.orderPostageFee)
      if (byFeeMatches.length === 1) return byFeeMatches[0].id
    }
  }

  return undefined
}

type PosVoucherPostageData = {
  deliveryMethod?: string | null
  postageOptionId?: string | null
  postageFee?: number | null
  isStorePickup?: boolean | null
}

type PosVoucherPostageLine = { voucherDeliveryData?: PosVoucherPostageData | null }

/** POS carts: the postage option already on the order — latest posted voucher wins, as on the backend. */
export function findSharedOrderPostageOptionIdFromPosLines(
  lines: PosVoucherPostageLine[],
): string | undefined {
  for (let index = lines.length - 1; index >= 0; index -= 1) {
    const delivery = lines[index].voucherDeliveryData
    if (!delivery || delivery.isStorePickup) continue
    if ((delivery.deliveryMethod ?? '').toUpperCase() !== 'PHYSICAL') continue
    if (delivery.postageOptionId) return delivery.postageOptionId
  }
  return undefined
}

/**
 * POS carts: a newly posted option replaces the order's single postage line, so
 * earlier lines stop carrying a fee and the cart counts postage once.
 */
export function appendPosVoucherLines<T extends PosVoucherPostageLine>(existing: T[], added: T[]): T[] {
  const replacesOrderPostage = added.some((line) => Boolean(line.voucherDeliveryData?.postageOptionId))
  if (!replacesOrderPostage) return [...existing, ...added]

  const superseded = existing.map((line) =>
    line.voucherDeliveryData?.postageFee
      ? ({ ...line, voucherDeliveryData: { ...line.voucherDeliveryData, postageFee: 0 } } as T)
      : line,
  )
  return [...superseded, ...added]
}

export function isPostedPhysicalVoucherDelivery(delivery?: PosVoucherPostageData | null): boolean {
  if (!delivery || delivery.isStorePickup) return false
  return (delivery.deliveryMethod ?? '').toUpperCase() === 'PHYSICAL'
}

export function posCartAllVouchersCollectInStore(
  lines: Array<{ serviceType?: string | null; voucherDeliveryData?: PosVoucherPostageData | null }>,
): boolean {
  return lines.every(
    (line) =>
      line.serviceType !== 'voucher' ||
      (line.voucherDeliveryData?.deliveryMethod ?? '').toUpperCase() !== 'PHYSICAL' ||
      line.voucherDeliveryData?.isStorePickup === true,
  )
}

export function buildOrderPostageFeeMap(
  detailsList: Array<{
    orderId: string
    discounts?: Array<{ discountType?: string; discountValue?: number | null }>
  }>,
): Map<string, number> {
  const map = new Map<string, number>()
  for (const details of detailsList) {
    map.set(details.orderId, sumOrderPostageDiscounts(details.discounts))
  }
  return map
}

export function posCartPostageFeesDiffer<T extends PosVoucherPostageLine>(before: T[], after: T[]): boolean {
  if (before.length !== after.length) return true
  return before.some((line, index) => {
    const left = line.voucherDeliveryData?.postageFee ?? 0
    const right = after[index].voucherDeliveryData?.postageFee ?? 0
    return left !== right
  })
}

/** Align cart postage fees with the order's single POSTAGE row (e.g. after voiding the carrier line). */
export type SyncPosCartPostageOptions = {
  /** When false, keep cart postage if the server snapshot still shows £0 (e.g. right after add). */
  clearWhenServerHasNoPostage?: boolean
}

export function syncPosCartPostageWithOrders<
  T extends PosVoucherPostageLine & { orderId?: string | null; serviceType?: string | null },
>(
  lines: T[],
  orderPostageByOrderId: Map<string, number>,
  options: SyncPosCartPostageOptions = {},
): T[] {
  const clearWhenServerHasNoPostage = options.clearWhenServerHasNoPostage === true
  const orderIds = new Set(
    lines.map((line) => line.orderId).filter((id): id is string => Boolean(id)),
  )

  const carrierIndexByOrder = new Map<string, number>()
  lines.forEach((line, index) => {
    if (
      !line.orderId ||
      line.serviceType !== 'voucher' ||
      !isPostedPhysicalVoucherDelivery(line.voucherDeliveryData) ||
      carrierIndexByOrder.has(line.orderId)
    ) {
      return
    }
    carrierIndexByOrder.set(line.orderId, index)
  })

  return lines.map((line, index) => {
    if (line.serviceType !== 'voucher' || !line.voucherDeliveryData || !line.orderId) return line

    const orderFee = orderPostageByOrderId.get(line.orderId) ?? 0
    const isCarrier = carrierIndexByOrder.get(line.orderId) === index

    if (!isPostedPhysicalVoucherDelivery(line.voucherDeliveryData) || !isCarrier) {
      if (!line.voucherDeliveryData.postageFee) return line
      if (!clearWhenServerHasNoPostage && orderFee <= 0) return line
      return {
        ...line,
        voucherDeliveryData: { ...line.voucherDeliveryData, postageFee: 0 },
      }
    }

    if (orderFee <= 0) {
      if (line.voucherDeliveryData.postageFee && !clearWhenServerHasNoPostage) return line
      if (!line.voucherDeliveryData.postageFee) return line
      return {
        ...line,
        voucherDeliveryData: { ...line.voucherDeliveryData, postageFee: 0 },
      }
    }

    if (line.voucherDeliveryData.postageFee === orderFee) return line
    return {
      ...line,
      voucherDeliveryData: { ...line.voucherDeliveryData, postageFee: orderFee },
    }
  })
}

export function sharedOrderPostageOptionNameFromOutstanding(
  discounts?: Array<{ discountType?: string; discountValue?: number | null; reason?: string | null }>,
): string | undefined {
  const row = (discounts ?? []).find(
    (entry) => entry.discountType === 'POSTAGE' && (entry.discountValue ?? 0) > 0,
  )
  const name = row?.reason?.trim()
  return name || undefined
}
